import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Partner = {
  id: string;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  partner_type: string;
  referral_code: string;
  status: string;
  notes: string | null;
  created_at: string;
};

const PARTNER_TYPES = ["hotel_concierge", "wedding_planner", "photographer", "travel_agent", "business", "other"] as const;
const STATUSES = ["active", "paused", "archived"] as const;

const TYPE_LABELS: Record<string, string> = {
  hotel_concierge: "Hotel Concierge",
  wedding_planner: "Wedding Planner",
  photographer: "Photographer",
  travel_agent: "Travel Agent",
  business: "Business",
  other: "Other",
};

const partnersQuery = queryOptions({
  queryKey: ["admin-referrers"],
  queryFn: async () => {
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return { isAdmin: false, rows: [] as Partner[], referralCounts: {} as Record<string, number> };
    const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: uid, _role: "admin" });
    if (!isAdmin) return { isAdmin: false, rows: [] as Partner[], referralCounts: {} as Record<string, number> };
    const { data, error } = await supabase
      .from("referral_partners")
      .select("id, business_name, contact_name, email, phone, partner_type, referral_code, status, notes, created_at")
      .order("created_at", { ascending: false });
    if (error) throw error;
    // Count partner form submissions per contact email as a simple referral signal.
    const { data: subs } = await supabase
      .from("contact_submissions")
      .select("email")
      .eq("form_type", "partner");
    const referralCounts: Record<string, number> = {};
    for (const s of subs ?? []) referralCounts[s.email] = (referralCounts[s.email] ?? 0) + 1;
    return { isAdmin: true, rows: (data ?? []) as Partner[], referralCounts };
  },
});

export const Route = createFileRoute("/_authenticated/admin/referrers")({
  head: () => ({
    meta: [
      { title: "Referral Partners | Avolus" },
      { name: "description", content: "Private list of Avolus referral partners." },
      { property: "og:title", content: "Referral Partners | Avolus" },
      { property: "og:description", content: "Private Avolus referral partner list." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(partnersQuery),
  errorComponent: ({ error }) => <p className="p-10 text-destructive">Couldn't load partners: {error instanceof Error ? error.message : "Unknown error"}</p>,
  notFoundComponent: () => <p className="p-10">Not found.</p>,
  component: Referrers,
});

const EMPTY_FORM = { business_name: "", contact_name: "", email: "", phone: "", partner_type: "other", referral_code: "", status: "active", notes: "" };

function Referrers() {
  const { data } = useSuspenseQuery(partnersQuery);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!data.isAdmin) {
    return (
      <main className="mx-auto max-w-xl p-10 text-center">
        <h1 className="text-2xl font-semibold">No access</h1>
        <p className="mt-3 text-muted-foreground">This account can't view referral partners. Sign in as info@avoluscars.com.</p>
        <button className="mt-6 underline" onClick={() => supabase.auth.signOut().then(() => location.assign("/auth?next=/admin/referrers"))}>
          Switch account
        </button>
      </main>
    );
  }

  const rows = data.rows.filter((r) => filter === "all" || r.status === filter);
  const count = (s: string) => data.rows.filter((r) => r.status === s).length;

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowForm(true);
  };

  const openEdit = (p: Partner) => {
    setEditing(p);
    setForm({
      business_name: p.business_name,
      contact_name: p.contact_name,
      email: p.email,
      phone: p.phone ?? "",
      partner_type: p.partner_type,
      referral_code: p.referral_code,
      status: p.status,
      notes: p.notes ?? "",
    });
    setError(null);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      business_name: form.business_name.trim(),
      contact_name: form.contact_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      partner_type: form.partner_type,
      referral_code: form.referral_code.trim().toUpperCase(),
      status: form.status,
      notes: form.notes.trim() || null,
    };
    if (!payload.business_name || !payload.contact_name || !payload.email || !payload.referral_code) {
      setError("Business name, contact name, email and referral code are required.");
      setSaving(false);
      return;
    }
    const res = editing
      ? await supabase.from("referral_partners").update(payload).eq("id", editing.id)
      : await supabase.from("referral_partners").insert(payload);
    setSaving(false);
    if (res.error) {
      setError(res.error.message.includes("referral_code") ? "That referral code is already in use." : res.error.message);
      return;
    }
    setShowForm(false);
    await queryClient.invalidateQueries({ queryKey: ["admin-referrers"] });
  };

  const remove = async (p: Partner) => {
    if (!window.confirm(`Remove ${p.business_name} from the partner list?`)) return;
    await supabase.from("referral_partners").delete().eq("id", p.id);
    await queryClient.invalidateQueries({ queryKey: ["admin-referrers"] });
  };

  const field = (label: string, key: keyof typeof EMPTY_FORM, type = "text") => (
    <label className="block text-sm">
      <span className="mb-1 block text-muted-foreground">{label}</span>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-md border border-border bg-background px-3 py-2"
      />
    </label>
  );

  return (
    <main className="mx-auto max-w-5xl px-5 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Referral Partners</h1>
          <p className="text-sm text-muted-foreground">
            Everyone who sends you bookings, with their referral code. Also see the <Link to="/admin/submissions" className="underline">form inbox</Link>.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={openAdd} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">Add partner</button>
          <button className="text-sm underline" onClick={() => supabase.auth.signOut().then(() => location.assign("/"))}>Sign out</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {(["all", ...STATUSES] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`rounded-full border border-border px-4 py-1.5 text-sm capitalize ${filter === s ? "bg-primary text-primary-foreground" : "bg-card text-foreground"}`}
          >
            {s === "all" ? `All (${data.rows.length})` : `${s} (${count(s)})`}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="mt-6 rounded-lg border border-border bg-card p-5">
          <h2 className="text-lg font-semibold">{editing ? `Edit ${editing.business_name}` : "Add a partner"}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {field("Business name", "business_name")}
            {field("Contact name", "contact_name")}
            {field("Email", "email", "email")}
            {field("Phone", "phone")}
            {field("Referral code (e.g. FAIRMONT)", "referral_code")}
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Partner type</span>
              <select value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2">
                {PARTNER_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2">
                {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="mb-1 block text-muted-foreground">Notes</span>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full rounded-md border border-border bg-background px-3 py-2" />
            </label>
          </div>
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
          <div className="mt-4 flex gap-3">
            <button onClick={save} disabled={saving} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
              {saving ? "Saving…" : editing ? "Save changes" : "Add partner"}
            </button>
            <button onClick={() => setShowForm(false)} className="text-sm underline">Cancel</button>
          </div>
        </div>
      )}

      <ul className="mt-6 divide-y divide-border rounded-lg border border-border bg-card">
        {rows.length === 0 && <li className="p-6 text-center text-muted-foreground">No partners yet. Click "Add partner" to create the first one.</li>}
        {rows.map((p) => (
          <li key={p.id} className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase tracking-wide text-muted-foreground">{TYPE_LABELS[p.partner_type] ?? p.partner_type}</span>
              <span className="font-medium">{p.business_name}</span>
              <span className="text-sm text-muted-foreground">{p.contact_name}</span>
              <span className={`ml-auto rounded px-2 py-0.5 text-xs capitalize ${p.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{p.status}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>Code: <span className="font-mono font-medium text-foreground">{p.referral_code}</span></span>
              <a className="underline" href={`mailto:${p.email}`}>{p.email}</a>
              {p.phone && <a className="underline" href={`tel:${p.phone}`}>{p.phone}</a>}
              <span>Applications: {data.referralCounts[p.email] ?? 0}</span>
              <span className="ml-auto flex gap-3">
                <button className="underline" onClick={() => openEdit(p)}>Edit</button>
                <button className="underline text-destructive" onClick={() => remove(p)}>Remove</button>
              </span>
            </div>
            {p.notes && <p className="mt-2 text-sm text-muted-foreground">{p.notes}</p>}
          </li>
        ))}
      </ul>
    </main>
  );
}
